<script setup lang="ts">
import { shallowRef, ref, computed, watch, onMounted, onUnmounted } from 'vue'

type DrawMode = 'select' | 'free' | 'line' | 'rect' | 'circle' | 'eraser'

const props = defineProps<{ context?: Record<string, any> }>()

const canvasEl = ref<HTMLCanvasElement>()
const fc = shallowRef<import('fabric').Canvas | null>(null)
const mode = ref<DrawMode>('free')
const color = ref('#ff0000')
const brushSize = ref(3)

// Fabric class references populated after dynamic import
let Fab: typeof import('fabric') | null = null

// Undo/redo history
const history: string[] = []
let histIdx = -1

// Shape draw state (non-reactive)
let isDrawing = false
let origX = 0
let origY = 0
let activeShape: import('fabric').FabricObject | null = null

// Stored handlers for clean removal
let _onDown: ((o: any) => void) | null = null
let _onMove: ((o: any) => void) | null = null
let _onUp: (() => void) | null = null

function removeShapeHandlers() {
  const canvas = fc.value
  if (!canvas) return
  if (_onDown) {
    canvas.off('mouse:down', _onDown)
    _onDown = null
  }
  if (_onMove) {
    canvas.off('mouse:move', _onMove)
    _onMove = null
  }
  if (_onUp) {
    canvas.off('mouse:up', _onUp)
    _onUp = null
  }
}

const backgroundDataUrl = computed(() => {
  const val = props.context?.node?.value
  if (!val) return ''
  return val.startsWith('data:') ? val : `data:image/png;base64,${val}`
})

function saveHistory() {
  if (!fc.value) return
  history.splice(histIdx + 1)
  history.push(JSON.stringify(fc.value.toJSON()))
  histIdx = history.length - 1
}

// Fabric 7 cria FabricImage com origem no centro por default; como fundo em
// (0,0) isso ancora o centro no canto e joga 3/4 da imagem para fora do canvas.
// Reancorar no canto superior esquerdo faz a imagem preencher o canvas.
function anchorTopLeft(img: import('fabric').FabricImage) {
  img.set({ originX: 'left', originY: 'top', left: 0, top: 0 })
}

async function loadBackground(canvas: import('fabric').Canvas) {
  if (!Fab || !backgroundDataUrl.value) return
  const img = await Fab.FabricImage.fromURL(backgroundDataUrl.value)
  anchorTopLeft(img)
  canvas.backgroundImage = img
  canvas.renderAll()
}

async function undo() {
  if (!fc.value || histIdx <= 0) return
  histIdx--
  await fc.value.loadFromJSON(JSON.parse(history[histIdx]))
  await loadBackground(fc.value)
  fc.value.renderAll()
}

async function redo() {
  if (!fc.value || histIdx >= history.length - 1) return
  histIdx++
  await fc.value.loadFromJSON(JSON.parse(history[histIdx]))
  await loadBackground(fc.value)
  fc.value.renderAll()
}

onMounted(async () => {
  Fab = await import('fabric')
  const { Canvas, PencilBrush, FabricImage } = Fab

  let w = 640
  let h = 480
  let bgImg: import('fabric').FabricImage | null = null

  if (backgroundDataUrl.value) {
    try {
      bgImg = await FabricImage.fromURL(backgroundDataUrl.value)
      w = bgImg.width
      h = bgImg.height
    } catch (error) {
      console.error('Error loading background image:', error)
    }
  }

  const canvas = new Canvas(canvasEl.value!, {
    backgroundColor: '#ffffff',
    width: w,
    height: h,
    selection: false
  })
  fc.value = canvas

  if (bgImg) {
    anchorTopLeft(bgImg)
    canvas.backgroundImage = bgImg
  }

  const brush = new PencilBrush(canvas)
  brush.color = color.value
  brush.width = brushSize.value
  canvas.freeDrawingBrush = brush
  canvas.isDrawingMode = true

  canvas.on('path:created', saveHistory)
  canvas.on('object:modified', saveHistory)

  // Aguarda o browser pintar os elementos wrapper do Fabric antes de renderizar
  await new Promise<void>(r => requestAnimationFrame(() => r()))
  canvas.calcOffset()
  canvas.requestRenderAll()

  saveHistory()
})

onUnmounted(() => {
  fc.value?.dispose()
})

function applyMode(newMode: DrawMode) {
  mode.value = newMode
  const canvas = fc.value
  if (!canvas || !Fab) return

  removeShapeHandlers()
  canvas.isDrawingMode = false
  canvas.selection = false

  if (newMode === 'free') {
    const { PencilBrush } = Fab
    if (!canvas.freeDrawingBrush)
      canvas.freeDrawingBrush = new PencilBrush(canvas)
    canvas.freeDrawingBrush.color = color.value
    canvas.freeDrawingBrush.width = brushSize.value
    canvas.isDrawingMode = true
  } else if (newMode === 'select') {
    canvas.selection = true
    canvas.getObjects().forEach((o) => {
      o.selectable = true
    })
  } else if (newMode === 'eraser') {
    _onDown = ({ target }: any) => {
      if (target) {
        canvas.remove(target)
        canvas.renderAll()
        saveHistory()
      }
    }
    canvas.on('mouse:down', _onDown)
  } else {
    setupShapeDraw(newMode)
  }
}

function setupShapeDraw(shapeMode: 'line' | 'rect' | 'circle') {
  const canvas = fc.value!
  const { Line, Rect, Circle } = Fab!

  _onDown = (opt: any) => {
    isDrawing = true
    const p = opt.scenePoint
    origX = p.x
    origY = p.y

    const base = {
      stroke: color.value,
      strokeWidth: brushSize.value,
      fill: 'transparent' as const,
      selectable: false,
      strokeUniform: true
    }

    if (shapeMode === 'line') {
      activeShape = new Line([p.x, p.y, p.x, p.y], { ...base, fill: color.value })
    } else if (shapeMode === 'rect') {
      // Centro fixo no clique; cursor sempre no canto oposto
      activeShape = new Rect({
        left: p.x,
        top: p.y,
        width: 0,
        height: 0,
        originX: 'center',
        originY: 'center',
        ...base
      })
    } else {
      // Centro fixo no clique; cursor sempre sobre a circunferência
      activeShape = new Circle({
        left: p.x,
        top: p.y,
        radius: 1,
        originX: 'center',
        originY: 'center',
        ...base
      })
    }

    canvas.add(activeShape)
  }

  _onMove = (opt: any) => {
    if (!isDrawing || !activeShape) return
    const p = opt.scenePoint
    const dx = p.x - origX
    const dy = p.y - origY

    if (shapeMode === 'line') {
      activeShape.set({ x2: p.x, y2: p.y } as any)
    } else if (shapeMode === 'rect') {
      // Cursor sempre no canto → largura/altura = 2× distância ao centro
      activeShape.set({
        width: Math.abs(dx) * 2,
        height: Math.abs(dy) * 2
      })
    } else {
      // Raio = distância do centro ao cursor → cursor sempre na borda
      const r = Math.sqrt(dx * dx + dy * dy)
      activeShape.set({ radius: r } as any)
    }

    canvas.renderAll()
  }

  _onUp = () => {
    if (!isDrawing) return
    isDrawing = false
    activeShape = null
    saveHistory()
  }

  canvas.on('mouse:down', _onDown)
  canvas.on('mouse:move', _onMove)
  canvas.on('mouse:up', _onUp)
}

watch(color, (val) => {
  if (fc.value?.freeDrawingBrush) fc.value.freeDrawingBrush.color = val
})

watch(brushSize, (val) => {
  if (fc.value?.freeDrawingBrush) fc.value.freeDrawingBrush.width = val
})

function clearCanvas() {
  if (!fc.value) return
  fc.value.getObjects().forEach(o => fc.value!.remove(o))
  fc.value.renderAll()
  props.context?.node?.input(null)
  saveHistory()
}

function saveCanvas() {
  if (!fc.value) return
  const dataURL = fc.value.toDataURL({ format: 'png', multiplier: 1 })
  props.context?.node?.input(dataURL.split(',')[1])
}
</script>

<template>
  <div class="border border-(--grid-border) rounded shadow-md overflow-hidden">
    <!-- Toolbar -->
    <div
      data-html2canvas-ignore
      class="flex items-center gap-1 flex-wrap p-2 bg-(--grid-chrome-bg) border-b border-(--grid-border)"
    >
      <UButton
        :color="mode === 'select' ? 'primary' : 'neutral'"
        icon="i-mdi-cursor-default"
        size="xs"
        square
        title="Selecionar"
        @click="applyMode('select')"
      />
      <UButton
        :color="mode === 'free' ? 'primary' : 'neutral'"
        icon="i-mdi-pencil"
        size="xs"
        square
        title="Traço Livre"
        @click="applyMode('free')"
      />
      <UButton
        :color="mode === 'line' ? 'primary' : 'neutral'"
        icon="i-mdi-minus"
        size="xs"
        square
        title="Reta"
        @click="applyMode('line')"
      />
      <UButton
        :color="mode === 'rect' ? 'primary' : 'neutral'"
        icon="i-mdi-rectangle-outline"
        size="xs"
        square
        title="Retângulo"
        @click="applyMode('rect')"
      />
      <UButton
        :color="mode === 'circle' ? 'primary' : 'neutral'"
        icon="i-mdi-circle-outline"
        size="xs"
        square
        title="Círculo"
        @click="applyMode('circle')"
      />
      <UButton
        :color="mode === 'eraser' ? 'primary' : 'neutral'"
        icon="i-mdi-eraser"
        size="xs"
        square
        title="Borracha"
        @click="applyMode('eraser')"
      />

      <div class="w-px h-6 bg-(--grid-border) mx-1" />

      <UButton
        icon="i-mdi-undo"
        size="xs"
        square
        color="neutral"
        title="Desfazer"
        @click="undo"
      />
      <UButton
        icon="i-mdi-redo"
        size="xs"
        square
        color="neutral"
        title="Refazer"
        @click="redo"
      />
      <UButton
        icon="i-mdi-trash-can-outline"
        size="xs"
        square
        color="neutral"
        title="Apagar tudo"
        @click="clearCanvas"
      />
      <UButton
        icon="i-mdi-content-save"
        size="xs"
        square
        color="neutral"
        title="Salvar"
        @click="saveCanvas"
      />

      <div class="w-px h-6 bg-(--grid-border) mx-1" />

      <input
        v-model="color"
        type="color"
        title="Cor"
        class="w-7 h-7 rounded cursor-pointer border border-(--grid-border) p-0"
      >

      <select
        v-model.number="brushSize"
        title="Espessura"
        class="text-xs border border-(--grid-border) rounded px-1 h-7"
      >
        <option :value="1">
          1px
        </option>
        <option :value="2">
          2px
        </option>
        <option :value="3">
          3px
        </option>
        <option :value="5">
          5px
        </option>
        <option :value="8">
          8px
        </option>
        <option :value="12">
          12px
        </option>
      </select>
    </div>

    <!-- Canvas -->
    <div class="flex justify-center overflow-auto min-h-25">
      <canvas ref="canvasEl" />
    </div>
  </div>
</template>
